import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import onsiteproLogo from "../../assets/logo2 2.png";
import AdminTaylorImportModal, { adminIsPrimaryAdmin } from "./AdminTaylorImportModal";
import AnalogClockTimePicker from "./AnalogClockTimePicker";
import { ensureFormUuid } from "../../utils/formUuid";
import { buildSharePointFileName, toDdMmYyyy, addedByName } from "../../utils/formPdfHelpers";

import { canEditDeleteRecords } from "../../utils/rolePermissions";
const THEME = "#8b1634";

function getBaseUrl() {
  const apiUrl = process.env.REACT_APP_API_URL;
  return apiUrl?.endsWith("/") ? apiUrl.slice(0, -1) : apiUrl || "";
}

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function parseStoredDate(value) {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const s = String(value).trim();
  const dmy = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (dmy) {
    const day = parseInt(dmy[1], 10);
    const month = parseInt(dmy[2], 10) - 1;
    const year = parseInt(dmy[3], 10);
    const d = new Date(year, month, day);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const ymd = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (ymd) {
    const year = parseInt(ymd[1], 10);
    const month = parseInt(ymd[2], 10) - 1;
    const day = parseInt(ymd[3], 10);
    const d = new Date(year, month, day);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

function dateToYmd(d) {
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function normalizeImageSource(value, fallbackMime = "image/png") {
  if (!value) return "";
  let src = value;
  if (Array.isArray(src)) src = src[0];
  if (src == null) return "";
  if (typeof src !== "string") src = String(src);
  src = src.trim();
  if (!src) return "";
  if (src.startsWith("data:image")) return src;
  if (/^(https?:)?\/\//i.test(src)) return src;
  const compact = src.replace(/\s+/g, "").replace(/,+$/, "");
  if (!compact) return "";
  return `data:${fallbackMime};base64,${compact}`;
}

function getPdfImageType(imageSrc, fallbackType = "PNG") {
  if (!imageSrc || typeof imageSrc !== "string") return fallbackType;
  const m = imageSrc.match(/^data:image\/([a-zA-Z0-9+.-]+);base64,/i);
  const subtype = m?.[1]?.toLowerCase();
  if (subtype === "png") return "PNG";
  if (subtype === "jpg" || subtype === "jpeg") return "JPEG";
  if (subtype === "webp") return "WEBP";
  return fallbackType;
}

const FIELD_STYLE = {
  border: "1px solid #cbd5e1",
  fontSize: 12,
  padding: "8px 10px",
  verticalAlign: "middle",
  background: "#fff"
};

const LABEL_STYLE = {
  ...FIELD_STYLE,
  width: 190,
  fontWeight: 700,
  color: THEME,
  background: "#f8f1f4"
};

// Word-document grid cells (matches the SF-015-F template layout)
const GRID_LABEL = {
  border: "1px solid #cbd5e1",
  fontSize: 12,
  padding: "8px 10px",
  fontWeight: 700,
  color: THEME,
  background: "#f8f1f4",
  width: "13%",
  verticalAlign: "middle"
};

const GRID_FIELD = {
  border: "1px solid #cbd5e1",
  fontSize: 12,
  padding: "6px 8px",
  background: "#fff",
  width: "20.3%",
  verticalAlign: "middle"
};

function renderSignatureBox(value) {
  return (
    <div
      style={{
        border: "1px dashed #8b1634",
        borderRadius: 8,
        background: "#fbeef2",
        minHeight: 64,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 4
      }}
    >
      {value ? (
        <img
          src={normalizeImageSource(value, "image/png")}
          alt="Signature"
          style={{ maxWidth: "100%", maxHeight: 56, objectFit: "contain" }}
          onError={(e) => {
            e.target.style.display = "none";
          }}
        />
      ) : (
        <span style={{ color: "#999", fontSize: 11 }}>No signature</span>
      )}
    </div>
  );
}

export default function BehaviouralExchangeEngineersPage() {
  const canEditDelete = canEditDeleteRecords();
  const [records, setRecords] = useState([]);
  const [loadingRecords, setLoadingRecords] = useState(true);
  const [appUsers, setAppUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const isAdminUser = adminIsPrimaryAdmin();
  const [editForm, setEditForm] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [uploadingKey, setUploadingKey] = useState(null);
  const [showSharePointUploadModal, setShowSharePointUploadModal] = useState(false);
  const [sharePointUploadRecord, setSharePointUploadRecord] = useState(null);
  const [sharePointPhaseType, setSharePointPhaseType] = useState("phase");
  const [sharePointPhaseNumber, setSharePointPhaseNumber] = useState("15");
  const [sharePointNetwork, setSharePointNetwork] = useState("");
  const [pendingDirectUpload, setPendingDirectUpload] = useState(false);
  useEffect(() => {
    if (pendingDirectUpload && sharePointUploadRecord) {
      setPendingDirectUpload(false);
      // eslint-disable-next-line no-use-before-define
      handleSharePointUpload();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingDirectUpload, sharePointUploadRecord]);

  useEffect(() => {
    fetchRecords();
    fetchAppUsers();
  }, []);

  async function fetchRecords() {
    setLoadingRecords(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + "/api/behavioural-exchange-engineers", {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setRecords(res.ok && data.success && Array.isArray(data.data) ? data.data : []);
    } catch {
      setRecords([]);
    } finally {
      setLoadingRecords(false);
    }
  }

  async function fetchAppUsers() {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + "/api/app-users", {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setAppUsers(res.ok && data.success && Array.isArray(data.data) ? data.data : []);
    } catch {
      setAppUsers([]);
    }
  }

  const filtered = records.filter((r) => {
    const fields = [
      r.interviewersName,
      r.intervieweeName,
      r.site,
      r.vehicleReg,
      r.interviewDate,
      r.activityType
    ]
      .map((v) => (v || "").toLowerCase());
    const words = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return words.length === 0 || words.every((w) => fields.some((field) => field.includes(w)));
  });
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paged = filtered.slice().slice((page - 1) * pageSize, page * pageSize);

  const handleEdit = (r) => {
    setEditForm({ ...r, formUuid: ensureFormUuid(r?.formUuid) });
    setShowEditModal(true);
  };

  const getUserName = (record) => {
    const uid = record?.userId;
    const u = appUsers.find((x) => x._id === uid);
    if (u) {
      const full = `${u.firstName || ""} ${u.lastName || ""}`.trim();
      return full || uid || "—";
    }
    const named = `${record?.addedByFirstName || ""} ${record?.addedByLastName || ""}`.trim();
    return named || uid || "—";
  };

  const handleEditSave = async () => {
    if (!editForm?._id) return;
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + "/api/behavioural-exchange-engineers/" + editForm._id, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          userId: editForm.userId,
          interviewersName: editForm.interviewersName,
          interviewersSigned: editForm.interviewersSigned,
          site: editForm.site,
          intervieweeName: editForm.intervieweeName,
          intervieweeSigned: editForm.intervieweeSigned,
          vehicleReg: editForm.vehicleReg,
          interviewDate: editForm.interviewDate,
          time: editForm.time,
          company: editForm.company,
          activityType: editForm.activityType,
          detailsOfConversation: editForm.detailsOfConversation,
          formUuid: ensureFormUuid(editForm.formUuid)
        })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        await fetchRecords();
        setShowEditModal(false);
        setEditForm(null);
        toast.success("Record updated!");
      } else {
        toast.error(data.message || "Failed to update");
      }
    } catch {
      toast.error("Failed to update");
    }
  };

  const handleDelete = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteId) {
      toast.error("Invalid record id");
      return;
    }
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + "/api/behavioural-exchange-engineers/" + deleteId, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setRecords(records.filter((f) => f._id !== deleteId));
        toast.success("Record deleted!");
      } else {
        toast.error(data.message || "Failed to delete");
      }
    } catch {
      toast.error("Failed to delete");
    }
    setShowDeleteModal(false);
    setDeleteId(null);
  };

  const handleSharePointUpload = async () => {
    const locationForUpload = (sharePointUploadRecord?.site || "").trim();
    if (!locationForUpload) {
      toast.error("Site is missing for this record.");
      return;
    }
    if (sharePointPhaseType === "phase" && (!sharePointNetwork || sharePointNetwork.trim() === "")) {
      toast.error("Please select Area.");
      return;
    }
    const payloadPhaseType = sharePointPhaseType === "phase" ? "phase" : "non-phase";
    const payloadNetwork = sharePointPhaseType === "phase" ? sharePointNetwork : "";
    const payloadPhaseNumber = sharePointPhaseType === "phase" ? sharePointPhaseNumber : "";
    setShowSharePointUploadModal(false);
    await generatePDF(sharePointUploadRecord, {
      uploadOnly: true,
      phaseType: payloadPhaseType,
      phaseNumber: payloadPhaseNumber,
      network: payloadNetwork,
      siteName: locationForUpload,
      onSuccess: () => {
        setSharePointUploadRecord(null);
        setSharePointPhaseType("phase");
        setSharePointPhaseNumber("15");
        setSharePointNetwork("");
      },
      onError: () => {
        setPendingDirectUpload(false);
      }
    });
  };

  const generatePDF = async (record, options = {}) => {
    const shouldUploadToSharePoint = options.uploadOnly === true;
    const uploadConfig = shouldUploadToSharePoint
      ? {
          phaseType: options.phaseType || "non-phase",
          phaseNumber: options.phaseNumber || "",
          network: options.network || "",
          siteName: options.siteName || "",
          onSuccess: options.onSuccess,
          onError: options.onError
        }
      : null;

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const primaryColor = [139, 22, 52];

    const drawRoundedRect = (x, y, width, height, radius, fillColor, strokeColor, hasShadow = false) => {
      if (hasShadow) {
        doc.setFillColor(0, 0, 0, 0.1);
        doc.roundedRect(x + 1, y + 1, width, height, radius, radius, "F");
      }
      doc.setFillColor(...fillColor);
      doc.setDrawColor(...strokeColor);
      doc.setLineWidth(0.5);
      doc.roundedRect(x, y, width, height, radius, radius, "FD");
    };

    const headerHeight = 30;
    drawRoundedRect(0, 0, pageWidth, headerHeight, 0, primaryColor, primaryColor);

    const addLogoToPDF = () =>
      new Promise((resolve) => {
        const logoImg = new Image();
        logoImg.crossOrigin = "anonymous";
        logoImg.onload = function onLoad() {
          try {
            const logoWidth = 70;
            const aspectRatio = logoImg.height / logoImg.width;
            const logoHeight = logoWidth * aspectRatio;
            const logoX = 10;
            const logoY = (headerHeight - logoHeight) / 2;
            const canvas = document.createElement("canvas");
            const scale = 2.5;
            canvas.width = logoImg.width * scale;
            canvas.height = logoImg.height * scale;
            const ctx = canvas.getContext("2d");
            ctx.scale(scale, scale);
            ctx.drawImage(logoImg, 0, 0);
            const highResDataUrl = canvas.toDataURL("image/png", 1.0);
            doc.addImage(highResDataUrl, "PNG", logoX, logoY, logoWidth, logoHeight, "", "NONE");
          } catch {
            doc.setFillColor(255, 255, 255);
            doc.rect(10, 6, 60, 15, "F");
            doc.setFont("helvetica", "bold");
            doc.setFontSize(10);
            doc.setTextColor(0, 0, 0);
            doc.text("TAYLOR TECHNOLOGY", 15, 12);
            doc.text("SYSTEMS LIMITED", 15, 17);
          }
          resolve();
        };
        logoImg.onerror = function onError() {
          resolve();
        };
        logoImg.src = onsiteproLogo;
      });

    await addLogoToPDF();

    const titleX = 90;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.setTextColor(255, 255, 255);
    doc.text("SF-015-F Behavioural Exchange Form", titleX, 13);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text("Engineers  -  TAYLOR TECHNOLOGY SYSTEMS LIMITED", titleX, 19);

    let yPosition = headerHeight + 10;
    const innerTableW = pageWidth - 20;
    const addedBy =
      record.addedByFirstName || record.addedByLastName
        ? `${record.addedByFirstName || ""} ${record.addedByLastName || ""}`.trim()
        : getUserName(record);

    const tableCommon = {
      styles: { font: "helvetica", fontSize: 9, cellPadding: 3, overflow: "linebreak", valign: "middle", lineColor: [203, 213, 225] },
      theme: "grid",
      tableWidth: innerTableW,
      margin: { left: 10, right: 10, bottom: 30 },
      bodyStyles: { lineWidth: 0.2, textColor: [0, 0, 0], lineColor: [203, 213, 225] }
    };

    const labelCell = (text) => ({ content: text, styles: { fillColor: [139, 22, 52], textColor: [255, 255, 255], fontStyle: "bold" } });
    const interviewerSigSrc = normalizeImageSource(record.interviewersSigned, "image/png");
    const intervieweeSigSrc = normalizeImageSource(record.intervieweeSigned, "image/png");

    autoTable(doc, {
      ...tableCommon,
      startY: yPosition,
      columnStyles: {
        0: { cellWidth: 36 },
        1: { cellWidth: 30 },
        2: { cellWidth: 22 },
        3: { cellWidth: 30 },
        4: { cellWidth: 28 },
        5: { cellWidth: 44 }
      },
      body: [
        [
          labelCell("Interviewers Name:"),
          { content: record.interviewersName || "" },
          labelCell("Signed:"),
          { content: "", styles: { minCellHeight: 20 } },
          labelCell("Site:"),
          { content: record.site || "" }
        ],
        [
          labelCell("Interviewee Name:"),
          { content: record.intervieweeName || "" },
          labelCell("Signed:"),
          { content: "", styles: { minCellHeight: 20 } },
          labelCell("Vehicle Reg:"),
          { content: record.vehicleReg || "" }
        ],
        [
          labelCell("Interview Date:"),
          { content: record.interviewDate || "" },
          labelCell("Time:"),
          { content: record.time || "" },
          labelCell("Company:"),
          { content: record.company || "Taylor Technology Systems Limited" }
        ],
        [
          labelCell("Activity Type:"),
          { content: record.activityType || "", colSpan: 5 }
        ]
      ],
      didDrawCell: (data) => {
        if (data.section !== "body" || data.column.index !== 3) return;
        let src = null;
        if (data.row.index === 0) src = interviewerSigSrc;
        else if (data.row.index === 1) src = intervieweeSigSrc;
        if (!src) return;
        const cell = data.cell;
        const maxW = cell.width - 4;
        const maxH = cell.height - 3;
        try {
          // Preserve aspect ratio (no stretch) + center → keeps the signature sharp.
          const props = doc.getImageProperties(src);
          const ratio = Math.min(maxW / props.width, maxH / props.height);
          const w = props.width * ratio;
          const h = props.height * ratio;
          const x = cell.x + (cell.width - w) / 2;
          const y = cell.y + (cell.height - h) / 2;
          doc.addImage(src, getPdfImageType(src, "PNG"), x, y, w, h, undefined, "NONE");
        } catch {}
      }
    });
    yPosition = doc.lastAutoTable.finalY + 4;

    doc.setFillColor(139, 22, 52);
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.rect(10, yPosition, pageWidth - 20, 8, "F");
    doc.text("Details of Conversation & Summary of Site", pageWidth / 2, yPosition + 5.5, { align: "center" });
    yPosition += 8;

    doc.setTextColor(0, 0, 0);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    const detailLines = doc.splitTextToSize(record.detailsOfConversation || "—", pageWidth - 24);
    const boxHeight = Math.max(60, detailLines.length * 4.8 + 8);
    doc.setDrawColor(180, 180, 180);
    doc.setLineWidth(0.2);
    doc.rect(10, yPosition, pageWidth - 20, boxHeight);
    doc.text(detailLines, 12, yPosition + 6);
    yPosition += boxHeight + 4;

    const totalPagesPdf = doc.getNumberOfPages();
    for (let p = 1; p <= totalPagesPdf; p += 1) {
      doc.setPage(p);
      const footerY = pageHeight - 25;
      drawRoundedRect(0, footerY, pageWidth, 25, 0, [248, 249, 250], [200, 200, 200]);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(`Generated on: ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}`, 20, footerY + 8);
      doc.text(`Page ${p} of ${totalPagesPdf}`, pageWidth - 35, footerY + 15);
    }

    const safeName = String(record.intervieweeName || "behavioural-exchange").replace(/[\\/:"*?<>|]+/g, "-");
    const safeDate = String(record.interviewDate || "date").replace(/[\\/:"*?<>|]+/g, "-");
    const fileName = shouldUploadToSharePoint
      ? buildSharePointFileName([record.intervieweeName, record.site, toDdMmYyyy(record.interviewDate)])
      : `SF015F_${safeName}_${safeDate}.pdf`;

    if (shouldUploadToSharePoint && uploadConfig) {
      try {
        const key = `${record._id || ""}|${record.site || ""}|${record.interviewDate || ""}`;
        setUploadingKey(key);
        const apiUrl = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
        const dataUri = doc.output("datauristring");
        const uploadParams = {
          fileName,
          fileBase64: dataUri,
          phaseType: uploadConfig.phaseType,
          phaseNumber: uploadConfig.phaseNumber,
          network: uploadConfig.network,
          siteName: uploadConfig.siteName, uploadContext: "hseq-forms-tablets", uploadSubFolder: "SF-015-F Behavioural Exchange Form - Engineers"
        };
        const res = await fetch(`${apiUrl}/api/sharepoint/upload`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` },
          body: JSON.stringify(uploadParams)
        });
        if (res.status !== 204 && !res.ok) {
          let msg = "SharePoint upload failed";
          try {
            const out = await res.json();
            msg = out?.message || msg;
          } catch {}
          throw new Error(msg);
        }
        toast.success("PDF uploaded to SharePoint successfully!");
        if (uploadConfig.onSuccess) uploadConfig.onSuccess();
      } catch (err) {
        toast.error(err?.message || "SharePoint upload failed");
        if (uploadConfig.onError) uploadConfig.onError(err);
      } finally {
        setUploadingKey(null);
      }
    } else {
      doc.save(fileName);
      toast.success("SF-015-F Behavioural Exchange Form - Engineers PDF generated successfully!");
    }
  };

  return (
    <>
      <div style={{ background: "#fff", borderRadius: 20, boxShadow: "0px 4px 20px rgba(0,0,0,0.08)", padding: 24, marginBottom: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24, flexWrap: "wrap", gap: 16 }}>
          <h2 style={{ fontSize: 24, fontWeight: 700, color: "#1a202c", margin: 0 }}>SF-015-F Behavioural Exchange Form - Engineers</h2>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            {isAdminUser && (
              <button
                type="button"
                onClick={() => setShowImportModal(true)}
                style={{
                  background: "#8b1634",
                  color: "#fff",
                  border: "none",
                  borderRadius: "10px",
                  padding: "10px 14px",
                  fontWeight: "600",
                  fontSize: "13px",
                  cursor: "pointer",
                }}
              >
                Import File
              </button>
            )}
          <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
            <i className="fa fa-search" style={{ position: "absolute", left: 12, color: THEME, fontSize: 14, pointerEvents: "none", zIndex: 1 }} />
            <input
              type="text"
              placeholder="Type to Search"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              style={{
                padding: "10px 12px 10px 36px",
                borderRadius: 12,
                border: "1px solid #e2e8f0",
                fontSize: 14,
                outline: "none",
                width: 250,
                boxSizing: "border-box"
              }}
            />
          </div>
          </div>
        </div>
        <div style={{ overflowX: "auto", borderRadius: 12, border: "1px solid #e2e8f0" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#f7fafc", borderBottom: "1px solid #e2e8f0" }}>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Interviewers Name</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Interviewee Name</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Site</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Vehicle Reg</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Interview Date</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Added By</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Updated Date</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loadingRecords ? (
                <tr>
                  <td colSpan={8} style={{ textAlign: "center", padding: 40, color: THEME, fontSize: 16, fontWeight: 600 }}>
                    <i className="fa fa-spinner fa-spin" style={{ marginRight: 8 }} />
                    Loading records...
                  </td>
                </tr>
              ) : paged.length === 0 ? (
                <tr>
                  <td colSpan={8} style={{ textAlign: "center", padding: 40, color: "#718096", fontSize: 16 }}>
                    No records found.
                  </td>
                </tr>
              ) : (
                paged.map((r, idx) => {
                  const updatedDate = r.updatedAt || r.createdAt;
                  return (
                    <tr key={r._id || idx} style={{ borderBottom: "1px solid #f1f5f9" }}>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{r.interviewersName || "—"}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{r.intervieweeName || "—"}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{r.site || "—"}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{r.vehicleReg || "—"}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{r.interviewDate || "—"}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{getUserName(r)}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{formatDate(updatedDate)}</td>
                      <td style={{ padding: 16 }}>
                        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                          {canEditDelete && (
                            <button type="button" onClick={() => handleEdit(r)} title="Edit" style={{ background: "transparent", border: "none", cursor: "pointer", padding: 6 }}>
                              <i className="fa fa-edit" style={{ color: "#22c55e", fontSize: 16 }} />
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => generatePDF(r)}
                            title="Generate PDF"
                            style={{ background: "transparent", border: "none", cursor: "pointer", padding: 6 }}
                          >
                            <i className="fa fa-file-pdf-o" style={{ color: "#dc2626", fontSize: 16 }} />
                          </button>
                          {(() => {
                            const k = `${r._id || ""}|${r.site || ""}|${r.interviewDate || ""}`;
                            const busy = uploadingKey === k;
                            return (
                              <button
                                type="button"
                                onClick={() => {
                                  if (!busy) {
                                    setSharePointUploadRecord(r);
                                    setSharePointPhaseType("non-phase"); setPendingDirectUpload(true);
                                  }
                                }}
                                disabled={busy}
                                title={busy ? "Uploading..." : "Upload to SharePoint"}
                                style={{
                                  background: "transparent",
                                  border: "none",
                                  cursor: busy ? "not-allowed" : "pointer",
                                  padding: 6,
                                  opacity: busy ? 0.6 : 1
                                }}
                              >
                                <i className={busy ? "fa fa-spinner fa-spin" : "fa fa-upload"} style={{ color: "#22c55e", fontSize: 16 }} />
                              </button>
                            );
                          })()}
                          {canEditDelete && (
                            <button type="button" onClick={() => handleDelete(r._id || r.id)} title="Delete" style={{ background: "transparent", border: "none", cursor: "pointer", padding: 6 }}>
                              <i className="fa fa-trash" style={{ color: "#e53e3e", fontSize: 16 }} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", marginTop: 18, gap: 12 }}>
          <button
            type="button"
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
            style={{
              padding: "8px 16px",
              borderRadius: 8,
              border: "1px solid #e2e8f0",
              background: page === 1 ? "#f7fafc" : "#fff",
              color: page === 1 ? "#a0aec0" : THEME,
              cursor: page === 1 ? "not-allowed" : "pointer"
            }}
          >
            Prev
          </button>
          <span style={{ fontWeight: 700, color: THEME, fontSize: 16 }}>
            {page} / {totalPages}
          </span>
          <button
            type="button"
            disabled={page === totalPages}
            onClick={() => setPage(page + 1)}
            style={{
              padding: "8px 16px",
              borderRadius: 8,
              border: "1px solid #e2e8f0",
              background: page === totalPages ? "#f7fafc" : "#fff",
              color: page === totalPages ? "#a0aec0" : THEME,
              cursor: page === totalPages ? "not-allowed" : "pointer"
            }}
          >
            Next
          </button>
        </div>
      </div>

      {showSharePointUploadModal && sharePointUploadRecord && (
        <div className="modal fade show" style={{ display: "block", background: "rgba(0,0,0,0.3)" }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{ maxWidth: "600px", margin: "0 auto" }}>
              <div className="modal-header">
                <h5 className="modal-title">Upload to SharePoint</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowSharePointUploadModal(false);
                    setSharePointUploadRecord(null);
                    setSharePointPhaseType("phase");
                    setSharePointPhaseNumber("15");
                    setSharePointNetwork("");
                  }}
                />
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="mb-3 col-12">
                    <label className="form-label fw-semibold">Type <span style={{ color: "red" }}>*</span></label>
                    <select
                      className="form-select"
                      value={sharePointPhaseType}
                      onChange={(e) => {
                        setSharePointPhaseType(e.target.value);
                        if (e.target.value === "non-phase") setSharePointNetwork("");
                      }}
                    >
                      <option value="phase">Phase</option>
                      <option value="non-phase">Non Phase</option>
                    </select>
                  </div>
                  {sharePointPhaseType === "phase" && (
                    <>
                      <div className="mb-3 col-md-6">
                        <label className="form-label fw-semibold">Phase Number <span style={{ color: "red" }}>*</span></label>
                        <select className="form-select" value={sharePointPhaseNumber} onChange={(e) => setSharePointPhaseNumber(e.target.value)}>
                          <option value="15">Phase 15 (2025)</option>
                          <option value="16">Phase 16 (2026)</option>
                        </select>
                      </div>
                      <div className="mb-3 col-md-6">
                        <label className="form-label fw-semibold">Area <span style={{ color: "red" }}>*</span></label>
                        <select className="form-select" value={sharePointNetwork} onChange={(e) => setSharePointNetwork(e.target.value)}>
                          <option value="">Select Area</option>
                          <option value="EPN">EPN</option>
                          <option value="LPN">LPN</option>
                          <option value="SPN">SPN</option>
                        </select>
                      </div>
                    </>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowSharePointUploadModal(false);
                    setSharePointUploadRecord(null);
                    setSharePointPhaseType("phase");
                    setSharePointPhaseNumber("15");
                    setSharePointNetwork("");
                  }}
                >
                  Cancel
                </button>
                <button type="button" className="btn" style={{ background: "#8b1634", color: "#fff", fontWeight: 600 }} onClick={handleSharePointUpload}>
                  Upload to SharePoint
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showEditModal && editForm && (
        <div className="modal fade show" style={{ display: "block", background: "rgba(0,0,0,0.3)" }} tabIndex="-1">
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content" style={{ maxWidth: 980, margin: "0 auto" }}>
              <div className="modal-header" style={{ background: THEME, color: "#fff" }}>
                <h5 className="modal-title">Edit SF-015-F Behavioural Exchange Form - Engineers</h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowEditModal(false)} />
              </div>
              <div className="modal-body" style={{ maxHeight: "75vh", overflowY: "auto", background: "#f8fafc", padding: 16 }}>
                <div style={{ marginBottom: 14, border: "1px solid #cbd5e1", borderRadius: 10, overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
                    <tbody>
                      <tr>
                        <td style={GRID_LABEL}>Interviewers Name:</td>
                        <td style={GRID_FIELD}>
                          <input className="form-control form-control-sm" value={editForm.interviewersName || ""} onChange={(e) => setEditForm({ ...editForm, interviewersName: e.target.value })} />
                        </td>
                        <td style={GRID_LABEL}>Signed:</td>
                        <td style={GRID_FIELD}>
                          {renderSignatureBox(editForm.interviewersSigned)}
                        </td>
                        <td style={GRID_LABEL}>Site:</td>
                        <td style={GRID_FIELD}>
                          <input className="form-control form-control-sm" value={editForm.site || ""} onChange={(e) => setEditForm({ ...editForm, site: e.target.value })} />
                        </td>
                      </tr>
                      <tr>
                        <td style={GRID_LABEL}>Interviewee Name:</td>
                        <td style={GRID_FIELD}>
                          <input className="form-control form-control-sm" value={editForm.intervieweeName || ""} onChange={(e) => setEditForm({ ...editForm, intervieweeName: e.target.value })} />
                        </td>
                        <td style={GRID_LABEL}>Signed:</td>
                        <td style={GRID_FIELD}>
                          {renderSignatureBox(editForm.intervieweeSigned)}
                        </td>
                        <td style={GRID_LABEL}>Vehicle Reg:</td>
                        <td style={GRID_FIELD}>
                          <input className="form-control form-control-sm" value={editForm.vehicleReg || ""} onChange={(e) => setEditForm({ ...editForm, vehicleReg: e.target.value })} />
                        </td>
                      </tr>
                      <tr>
                        <td style={GRID_LABEL}>Interview Date:</td>
                        <td style={GRID_FIELD}>
                          <DatePicker
                            selected={parseStoredDate(editForm.interviewDate)}
                            onChange={(d) => setEditForm({ ...editForm, interviewDate: d ? dateToYmd(d) : "" })}
                            dateFormat="dd-MM-yyyy"
                            className="form-control form-control-sm"
                            placeholderText="Select date"
                          />
                        </td>
                        <td style={GRID_LABEL}>Time:</td>
                        <td style={GRID_FIELD}>
                          <AnalogClockTimePicker
                            id="behavioural-exchange-time"
                            value={editForm.time || ""}
                            onChange={(time) => setEditForm({ ...editForm, time })}
                            isClearable
                            placeholder="Select time"
                            aria-label="Time"
                          />
                        </td>
                        <td style={GRID_LABEL}>Company:</td>
                        <td style={GRID_FIELD}>
                          <input className="form-control form-control-sm" value={editForm.company || ""} onChange={(e) => setEditForm({ ...editForm, company: e.target.value })} />
                        </td>
                      </tr>
                      <tr>
                        <td style={GRID_LABEL}>Activity Type:</td>
                        <td style={GRID_FIELD} colSpan={5}>
                          <input className="form-control form-control-sm" value={editForm.activityType || ""} onChange={(e) => setEditForm({ ...editForm, activityType: e.target.value })} />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div style={{ marginBottom: 8, border: "1px solid #cbd5e1", borderRadius: 10, overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
                  <div style={{ fontWeight: 700, background: "#efe2e7", color: THEME, fontSize: 13, padding: "8px 10px", textAlign: "center" }}>Details of Conversation &amp; Summary of Site</div>
                  <div style={{ padding: 8, background: "#fff" }}>
                    <textarea className="form-control form-control-sm" rows={12} value={editForm.detailsOfConversation || ""} onChange={(e) => setEditForm({ ...editForm, detailsOfConversation: e.target.value })} />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowEditModal(false)}>
                  Cancel
                </button>
                <button type="button" className="btn" style={{ background: THEME, color: "#fff", fontWeight: 600 }} onClick={handleEditSave}>
                  Submit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <AdminTaylorImportModal
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        title="Upload SF-015-F Behavioural Exchange Form - Engineers File"
        importPath="behavioural-exchange-engineers/import-form"
        isAdminUser={isAdminUser}
        onImported={({ insertedRow }) => {
          if (insertedRow && insertedRow._id) {
            setRecords((prev) => [insertedRow, ...prev]);
            setPage(1);
          }
        }}
      />

      {showDeleteModal && (
        <div className="modal fade show" style={{ display: "block", background: "rgba(0,0,0,0.3)" }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Delete SF-015-F Behavioural Exchange Form - Engineers</h5>
                <button type="button" className="btn-close" onClick={() => setShowDeleteModal(false)} />
              </div>
              <div className="modal-body">Are you sure you want to delete this record?</div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>
                  Cancel
                </button>
                <button type="button" className="btn btn-danger" onClick={confirmDelete}>
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
